import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { activeTotal } from '../../utils/memberStatus';
import { COLORS, pct, bucketByPeriod } from './signupReportUtils';

const RATES = [
  // Active = Approved + Hidden (a total, not a status).
  ['active', '% Active', COLORS.active, (b) => pct(activeTotal(b), b.signed)],
  ['approved', '% Approved', COLORS.approved, (b) => pct(b.approved, b.signed)],
  ['hidden', '% Hidden', COLORS.hidden, (b) => pct(b.hidden, b.signed)],
  ['inactive', '% Inactive', COLORS.inactive, (b) => pct(b.inactive, b.signed)],
  ['deleted', '% Deleted', COLORS.deleted, (b) => pct(b.deleted, b.signed)],
  ['deactivated', '% Deactivated', COLORS.deactivated, (b) => pct(b.deactivated, b.signed)],
  ['suspended', '% Suspended', COLORS.suspended, (b) => pct(b.suspended, b.signed)],
];

const MiniRate = ({ name, color, labels, data }) => {
  const now = data.length ? data[data.length - 1] : 0;
  const options = {
    chart: { type: 'area', height: 110, backgroundColor: 'transparent', margin: [6, 4, 22, 28] },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: { categories: labels, labels: { style: { fontSize: '9px', color: '#A3A3A3' }, step: Math.ceil(labels.length / 5) || 1 }, lineWidth: 0, tickLength: 0 },
    yAxis: { min: 0, title: { text: null }, gridLineColor: '#F4F4F4', labels: { format: '{value}%', style: { fontSize: '9px', color: '#A3A3A3' } } },
    tooltip: { formatter() { return `${this.x}: <b>${this.y}%</b>`; } },
    plotOptions: {
      area: {
        lineWidth: 2.5,
        lineColor: color,
        fillColor: `${color}22`,
        marker: { enabled: false, states: { hover: { enabled: true, radius: 4 } } },
      },
    },
    series: [{ data }],
  };

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #EAEAEA', borderRadius: '14px', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#404040' }}>{name}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.5px', color }}>{now}%</Typography>
      </Box>
      {labels.length ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <Typography sx={{ fontSize: 11, color: '#A3A3A3', textAlign: 'center', py: 2 }}>No data</Typography>
      )}
    </Box>
  );
};

const SignupStageRates = ({ byDate = [] }) => {
  const buckets = useMemo(() => bucketByPeriod(byDate, 'weekly'), [byDate]);
  const labels = buckets.map((b) => b.label);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: '#737373', mb: 1.5 }}>
        Stage rates over time (weekly)
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {RATES.map(([key, label, color, fn]) => (
          <MiniRate key={key} name={label} color={color} labels={labels} data={buckets.map(fn)} />
        ))}
      </Box>
    </Box>
  );
};

export default SignupStageRates;
